import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, FormGroup, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { AbcCore, ConvertHelper } from '@barthauer/abc/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { AbcTextBoxComponent } from '@barthauer/abc/ui-components';
import { from, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { AuthenticationInterceptor } from '../interceptor/interceptor';
import { AuthenticationLevelMode, AuthenticationProviderType, LoginInfo } from '../models/login-info.model';
import { UserService } from '../services/user.service';
import { WebApiInfoService } from '../services/webapi-info.service';
import { WebApiInfo } from './../models/webapi-info.model';
enum InputState {
  ready = 0,
  reset = 1,
}

enum LoginViewMode {
  Login,
  SecondFactor,
  ChangePassword,
}

class LoginViewState {
  private mode: LoginViewMode = LoginViewMode.Login;
  public readonly secondFactor = new SecondFactorViewState();

  public change(mode: LoginViewMode) {
    if (mode === LoginViewMode.SecondFactor) {
      throw new Error('Use changeSecondFactor function to switch to secondFactor view');
    }
    this.mode = mode;
  }

  public changeSecondFactor(mode: SecondFactorViewMode) {
    this.mode = LoginViewMode.SecondFactor;
    this.secondFactor.internalChange(mode);
  }

  public get showLogin(): boolean {
    return this.mode === LoginViewMode.Login;
  }

  public get showSecondFactor(): boolean {
    return this.mode === LoginViewMode.SecondFactor && this.secondFactor.enabled;
  }

  public get showChangePassword(): boolean {
    return this.mode === LoginViewMode.ChangePassword;
  }
}

enum SecondFactorViewMode {
  None,
  InvalidConfig,
  ShouldSetup,
  Setup,
  SetupInvalidCode,
  SetupDeprecated,
  Input,
  InvalidCode,
}

class SecondFactorViewState {
  private mode: SecondFactorViewMode = SecondFactorViewMode.None;

  public internalChange(mode: SecondFactorViewMode) {
    this.mode = mode;
  }

  public get enabled(): boolean {
    return this.mode !== SecondFactorViewMode.None;
  }

  public get showInput(): boolean {
    return this.isSetup || this.input || this.invalidCode;
  }

  public get showQRCode(): boolean {
    return this.isSetup;
  }

  private get isSetup(): boolean {
    return this.setup || this.setupDeprecated || this.setupInvalidCode;
  }

  public get showInvalidCodeMessage(): boolean {
    return this.invalidCode || this.setupInvalidCode;
  }

  public get invalidConfig(): boolean {
    return this.mode === SecondFactorViewMode.InvalidConfig;
  }

  public get shouldSetup(): boolean {
    return this.mode === SecondFactorViewMode.ShouldSetup;
  }

  public get setup(): boolean {
    return this.mode === SecondFactorViewMode.Setup;
  }

  public get setupInvalidCode(): boolean {
    return this.mode === SecondFactorViewMode.SetupInvalidCode;
  }

  public get setupDeprecated(): boolean {
    return this.mode === SecondFactorViewMode.SetupDeprecated;
  }

  public get input(): boolean {
    return this.mode === SecondFactorViewMode.Input;
  }

  public get invalidCode(): boolean {
    return this.mode === SecondFactorViewMode.InvalidCode;
  }
}

@Component({
  selector: 'abc-authentication-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.Emulated, // allows styles in injected HTML
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
  public resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;
  public username: AbstractControl;
  public password: AbstractControl;
  public autoDisplayButtons: boolean = false;
  public showLoader: boolean = false;
  public secondFactorInput: boolean = false;
  public confirmationcodeFilled: boolean = false;
  public mFASetupInvalid: boolean = false;
  public loginLockoutWarning: boolean = false;
  public loginLockout: boolean = false;
  public lockoutRetries: number;
  public lockoutSeconds: number;
  public lockoutEnd: Date;
  public lockoutTimer: number;

  public get lockoutEndString(): string {
    return this.lockoutEnd?.toLocaleTimeString(ResourceManagerService.getCurrentLanguage());
  }

  public placeholders: string[] = ['0', '0', '0', '0', '0', '0'];
  @ViewChildren('input') inputsList!: QueryList<ElementRef>;
  // autocapitalize?: string;
  // isCodeHidden: boolean = true;
  public inputMode!: string;
  public inputType!: string;
  public twoFactorCode: string;
  private codeLength!: number;
  private inputs: HTMLInputElement[] = [];

  // codeChanged: string;
  private _codeLength: number = 6;
  private initialFocusField?: number;
  private isFocusingOnLastByClickIfFilled: boolean = true;
  private isCharsCode: boolean = true;
  private isNonDigitsCode = false;
  private inputsStates: InputState[] = [];
  @ViewChild('input', { static: false }) input: AbcTextBoxComponent;
  @ViewChild('submitButton', { static: false }) submitButton: ElementRef;
  private isPrevFocusableAfterClearing: boolean = true;
  private state = {
    isFocusingAfterAppearingCompleted: false,
    isInitialFocusFieldEnabled: false,
  };
  private inputsListSubscription!: Subscription;
  private code?: string | number;

  Form: UntypedFormGroup;
  errorExist = false;
  ApplicationTitle: string;
  LoginIntroHtml: string; // = 'BaS<span style="color:rgba(175, 202, 5, 1);">Y</span>S Login';

  ForwardToRoutePath: string;
  ForceReloadOnApply: string;

  @Input() errorClass: string;
  @Input() logoUrl: string;
  @Input() specialLogin: boolean;
  @Input() basysLogin: boolean;

  ErrorMessage: string;
  mode: ProgressSpinnerMode;
  isChangingWebApiSupported: boolean;
  isAllowBaseURLChange: boolean;
  Errors = false;
  value: any;
  hide: boolean;
  right: any;
  pw_vis: boolean;
  isPasswordChanged: boolean;
  @Input() loaderValue: number = 0;
  @Input() isPasswordForgotten: boolean = false;
  @Output() onNeedsPasswordChange: EventEmitter<void> = new EventEmitter();

  private loginInfo: LoginInfo;

  currentWebApiInfo$: Observable<WebApiInfo>;
  private destroy$ = new Subject<void>(); // used to cancel Observable chains when component is destroyed
  rtlEnabled: boolean;

  formGroup: FormGroup;
  public secret: string;
  public issuer: string;
  public baseUrl: string;
  public viewState: LoginViewState = new LoginViewState();
  public get secondFactor(): SecondFactorViewState {
    return this.viewState.secondFactor;
  }
  public secondFactorMode: AuthenticationLevelMode;

  constructor(
    private fb: UntypedFormBuilder,
    private webApiInfoService: WebApiInfoService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef,
    /* private baseReq: RequestOptionsService*/
    private configService: AbcConfigService,
    private abcCore: AbcCore,
    private elementRef: ElementRef,
    private resourceService: ResourceManagerService
  ) {
    this.resAsync = this.resourceService.getResourceSet('abc-core-authentication');
    this.rtlEnabled = this.resourceService.isRightToLeftEnabled;
    this.createForm();
    this.isChangingWebApiSupported = this.webApiInfoService.isChangingWebApiSupported(this.router);
    this.isAllowBaseURLChange = <boolean>this.configService.configuration?.WebApi?.AllowBaseURLChange;
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.ApplicationTitle = data.ApplicationTitle;
        this.ForwardToRoutePath = data.ForwardTo;
        this.ForceReloadOnApply = data.ForceReloadOnApply;
        if (data.LoginIntroHtml) {
          this.LoginIntroHtml = data.LoginIntroHtml;
        }
      },
      error: (err) => this.abcCore.error.publish(err),
    });

    if (document.getElementsByClassName('percentage')[0]) {
      document.getElementsByClassName('percentage')[0]['style'].display = 'none';
      document.getElementsByClassName('circle')[0]['style'].display = 'none';
    }
  }

  async ngOnInit() {
    this.state.isInitialFocusFieldEnabled = !this.isEmpty(this.initialFocusField);

    this.onCodeLengthChanges();
    this.res = await this.resAsync;
    this.currentWebApiInfo$ = this.webApiInfoService
      .getCurrentWebApiInfo()
      .pipe(
        catchError((err) =>
          of(WebApiInfo.CreateInvalidWebApiBaseURLInfo(this.configService.configuration.WebApi.BaseURL))
        )
      );

    const loginIntroTextDiv = document.getElementById('LoginIntroText');
    if (loginIntroTextDiv && this.LoginIntroHtml) {
      loginIntroTextDiv.innerHTML = this.LoginIntroHtml;
    }
  }

  ngAfterViewInit(): void {
    this.inputsListSubscription = this.inputsList.changes.subscribe(this.onInputsListChanges.bind(this));
    this.onInputsListChanges(this.inputsList);
    this.changeDetectorRef.detectChanges();
    this.checkFields((state) => {
      if (state) {
        this.autoDisplayButtons = true;
      }
    });
  }

  private checkFields(callback: any) {
    let i = 0;

    let timer = setInterval(() => {
      i++;
      const pw = document.querySelector('#password:-webkit-autofill');
      if (pw != null) {
        clearInterval(timer);
        callback(true);
      } else if (i > 10) {
        clearInterval(timer);
        callback(false);
      }
    }, 200);
  }

  ngOnDestroy(): void {
    this.destroy$.next(); // trigger the unsubscribe
    this.destroy$.complete(); // finalize & clean up the subject stream

    if (this.inputsListSubscription) {
      this.inputsListSubscription.unsubscribe();
    }
  }

  createForm() {
    this.Form = this.fb.group({
      username: [''],
      password: [''],
    });
    this.username = this.Form.controls['username'];
    this.password = this.Form.controls['password'];
  }

  public clearLastValidation() {
    this.errorExist = false;
    this.Errors = false;
    this.ErrorMessage = null;
    // if (this.username.value.length ==0 && this.password.value.length ==0){
    //   this.ErrorMessage=null;

    // }
  }

  onSubmit(): void {
    clearInterval(this.lockoutTimer);
    this.clearLastValidation();
    this.errorExist = true;

    if (this.Form.valid) {
      this.showLoader = true;
      this.loaderValue = 10;

      // for (let index = 0; index < 80; index++) {
      //   this.loaderValue ++;
      // }

      this.loaderValue = 10;

      this.loginInfo = undefined;
      this.userService
        .loginUser(this.username.value, this.password.value)
        .pipe(takeUntil(this.destroy$)) // Avoid login and routing if page was left before server response arrived
        .subscribe({
          next: async () => {
            let routePath: string;
            if (this.ForwardToRoutePath) {
              routePath = this.ForwardToRoutePath;
              // } else if (
              //   this.configService &&
              //   this.configService.configuration &&
              //   this.configService.configuration.DefaultRoute
              // ) {
              //   routePath = this.configService.configuration.DefaultRoute;
            } else {
              routePath = '/';
            }

            this.loaderValue = 50;
            this.baseUrl = this.configService.getWebApiBaseURL();
            const loginInfo = await this.ensureCurrentLoginInfo();

            switch (loginInfo.secondFactorMode) {
              case AuthenticationLevelMode.Confirmed:
              case AuthenticationLevelMode.ConfirmedDeprecated:
                this.setupSecondFactor(SecondFactorViewMode.Input, loginInfo.secondFactorMode);
                break;
              case AuthenticationLevelMode.Invalid:
                this.viewState.changeSecondFactor(SecondFactorViewMode.InvalidConfig);
                break;
              case AuthenticationLevelMode.None:
                this.viewState.changeSecondFactor(SecondFactorViewMode.None);
                await this.rerouteLogin(routePath);
                break;
              case AuthenticationLevelMode.Optional:
                this.setupSecondFactor(SecondFactorViewMode.ShouldSetup, AuthenticationLevelMode.Optional);
                break;
              case AuthenticationLevelMode.RequiredToConfirm:
                await this.requireSecondFactor(false);
                break;
              default:
                await this.rerouteLogin(routePath);
                break;
            }

            // if (this.ForceReloadOnApply) {
            //   AuthenticationInterceptor.reload(routePath);
            // } else {
            //   this.router.navigate([routePath]);
            // }

            // let navi = ['/'];

            // if (this.ForwardToRoutePath) {
            //   if (this.route.data && this.route.snapshot.data.mainOutletName) {
            //     navi = JSON.parse(
            //       `[{ "outlets": { "${this.route.snapshot.data.mainOutletName}": "${this.ForwardToRoutePath}"}}]`
            //     );
            //   } else if (this.route.outlet !== 'primary' && this.ForwardToRoutePath !== '/') {
            //     navi = JSON.parse(`[{ "outlets": { "${this.route.outlet}": "${this.ForwardToRoutePath}"}}]`);
            //   } else {
            //     navi = [this.ForwardToRoutePath];
            //   }
            // } else if (
            //   this.configService &&
            //   this.configService.configuration &&
            //   this.configService.configuration.DefaultRoute
            // ) {
            //   navi = [this.configService.configuration.DefaultRoute];
            // }

            // this.router.navigate(navi);

            // }, 3000);
          },
          error: (err) => {
            if (err.error.error === 'invalid_config') {
              this.viewState.changeSecondFactor(SecondFactorViewMode.InvalidConfig);
            } else {
              this.showLoader = false;
              this.ErrorMessage = err.message;
              if (err.error?.error_description) {
                this.Errors = true;
                // const settings = new MessageSettings();
                // settings.mode = 2;
                // settings.button = 1;
                // settings.message = err.error.error_description
                //   ? err.error.error_description
                //   : 'ERR_INTERNET_DISCONNECTED';
                // this.messageBoxService
                //   .showMessageBox(settings)
                //   .subscribe((result) => console.log(result));
                const lockoutEnd = err.headers?.get('Lockout-End');
                const lockoutRetries = err.headers?.get('Lockout-Counter');
                if (lockoutEnd) {
                  console.warn('Account is locked until: ' + ConvertHelper.fromString(Date, lockoutEnd));
                  this.lockoutEnd = <Date>ConvertHelper.fromString(Date, lockoutEnd);
                  this.loginLockoutWarning = false;
                  this.loginLockout = true;
                  this.lockoutSeconds = parseInt(err.headers.get('Lockout-Seconds'));
                  this.lockoutTimer = setInterval(() => {
                    this.lockoutSeconds--;
                  }, 1000);
                  setTimeout(() => {
                    clearInterval(this.lockoutTimer);
                    this.loginLockout = false;
                    this.loginLockoutWarning = false;
                  }, parseInt(err.headers.get('Lockout-Seconds')) * 1000);
                } else if (lockoutRetries) {
                  console.warn('Account will be locked after: ' + lockoutRetries);
                  this.lockoutRetries = lockoutRetries;
                  this.loginLockout = false;
                  this.loginLockoutWarning = true;
                }
                this.ErrorMessage = err.error.error_description;
              }
            }

            this.changeDetectorRef.detectChanges();
          },
          complete: () => {
            this.loaderValue = 100;
          },
        });
    }
  }

  private async requireSecondFactor(isDeprecated: boolean): Promise<void> {
    if (!this.secret || isDeprecated) {
      this.issuer = encodeURIComponent(
        this.res.get('SecondFactor-IssuerFormat', await this.userService.getSystemName())
      );
      this.secret = await this.userService.updateIdentity(AuthenticationProviderType.AppTotp, '');
    }
    this.setupSecondFactor(
      isDeprecated ? SecondFactorViewMode.SetupDeprecated : SecondFactorViewMode.Setup,
      AuthenticationLevelMode.RequiredToConfirm
    );
  }

  async onSubmitButtonKeydown(e: any): Promise<void> {
    if (this.isEnterKey(e)) {
      this.submitButton?.nativeElement.click();
    }
  }

  // checkConfirmationcodeFilled() {
  //   this.confirmationcodeFilled = this.placeholders.length === this.getCurrentFilledCode().length;

  //   if (this.confirmationcodeFilled) {
  //     setTimeout(() => {
  //       // this.confirmSecondFactor();
  //       this.submitButton?.nativeElement.focus();
  //     }, 10);
  //   }
  // }

  async setOptionalSecondFactor() {
    await this.requireSecondFactor(false);
  }

  private setupSecondFactor(viewMode: SecondFactorViewMode, mode?: AuthenticationLevelMode) {
    for (let i = 0; i < this.inputs?.length; i++) {
      this.inputs[i].value = '';
    }
    this.viewState.changeSecondFactor(viewMode);
    if (mode) {
      this.secondFactorMode = mode;
    }
    this.changeDetectorRef.detectChanges();
    setTimeout(() => {
      if (this.inputs?.length) {
        this.inputs[0].focus();
      }
    }, 10);
  }

  async confirmSecondFactor() {
    const routePath: string = this.ForwardToRoutePath || '/';
    switch (this.secondFactorMode) {
      case AuthenticationLevelMode.Optional:
      case AuthenticationLevelMode.Invalid:
        await this.rerouteLogin(routePath);
        break;
      case AuthenticationLevelMode.RequiredToConfirm:
        if (this.confirmationcodeFilled) {
          try {
            await this.userService.updateIdentity(AuthenticationProviderType.AppTotp, '', this.twoFactorCode);
            await this.userService
              .loginUser(
                this.username.value,
                this.password.value,
                this.twoFactorCode,
                this.getOtpIdentifier(AuthenticationProviderType.AppTotp)
              )
              .toPromise();
            this.setupSecondFactor(SecondFactorViewMode.None, AuthenticationLevelMode.Confirmed);
            await this.rerouteLogin(routePath, true);
          } catch (error) {
            this.setupSecondFactor(SecondFactorViewMode.SetupInvalidCode);
            return;
          }
        }
      case AuthenticationLevelMode.Confirmed:
      case AuthenticationLevelMode.ConfirmedDeprecated:
        if (this.confirmationcodeFilled) {
          this.userService
            .loginUser(
              this.username.value,
              this.password.value,
              this.twoFactorCode,
              this.getOtpIdentifier(AuthenticationProviderType.AppTotp)
            )
            .subscribe(
              async () => {
                if (this.secondFactorMode == AuthenticationLevelMode.Confirmed) {
                  await this.rerouteLogin(routePath);
                } else {
                  await this.requireSecondFactor(true);
                }
              },
              (error) => {
                console.log('Confirmation error', error);
                this.setupSecondFactor(SecondFactorViewMode.InvalidCode);
              }
            );
        }
        break;
    }
  }
  async cancelSecondFactor() {
    window.location.reload();
  }
  private getOtpIdentifier(type: AuthenticationProviderType): string {
    switch (type) {
      case AuthenticationProviderType.AppTotp:
        return 'app_totp';
      case AuthenticationProviderType.Email:
        return 'email_otp';
      case AuthenticationProviderType.Phone:
        return 'phone_otp';
    }
  }

  public async rerouteLogin(routePath: string, refreshLoginInfo?: boolean) {
    if (refreshLoginInfo) {
      this.loginInfo = undefined;
    }
    const loginInfo = await this.ensureCurrentLoginInfo();

    if (loginInfo.needsPasswordChange) {
      this.loginInfo = undefined;
      this.onNeedsPasswordChange.emit();
      this.viewState.change(LoginViewMode.ChangePassword);
    } else {
      if (this.ForceReloadOnApply) {
        AuthenticationInterceptor.reload(routePath);
      } else {
        this.router.navigate([routePath]);
      }
    }
  }

  back() {
    this.username.setValue('');
    this.password.setValue('');
    this.ErrorMessage = null;
  }

  restorePassword() {
    alert('TODO');
  }

  editWebAPI() {
    from(this.webApiInfoService.navigateToWebApiEditorPage(this.router, this.route, '/'))
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: (err) => this.abcCore.error.publish(err) });
  }

  onInputClick() {
    if (this.basysLogin) {
      let element = this.elementRef.nativeElement.parentElement.parentElement;
      element.parentElement.classList.remove('passwordStyle');
      element.classList.remove('blurStyle');
      element.classList.add('clickStyle');
    }
  }

  onInputLeave(e) {
    if (this.basysLogin) {
      if (e.relatedTarget == null) {
        let element = this.elementRef.nativeElement.parentElement.parentElement;
        element.classList.remove('clickStyle');
        element.classList.add('blurStyle');
        if (this.hasPassword && this.hasUserName) {
          element.parentElement.classList.add('passwordStyle');
        }
      }
    }
  }

  onClearClick() {
    this.Errors = false;
    if (this.basysLogin) {
      let element = this.elementRef.nativeElement.parentElement.parentElement;
      element.parentElement.classList.remove('passwordStyle');
    }
  }

  onPasswordBeforeInput() {
    if (!this.isPasswordChanged) {
      this.isPasswordChanged = true;
      this.password.setValue('');
    }
  }

  onPasswordFocus($event) {
    if (!this.isPasswordChanged && !this.pw_vis) {
      $event.target.select();
    }
  }

  public get hasUserName(): boolean {
    return this.username.value?.length > 0;
  }

  public get hasPassword(): boolean {
    return this.password.value?.length > 0;
  }

  onClick(e: any): void {
    // handle click events only if the the prop is enabled
    if (!this.isFocusingOnLastByClickIfFilled) {
      return;
    }

    const target = e.target;
    const last = this.inputs[this._codeLength - 1];
    // already focused
    if (target === last) {
      return;
    }

    // check filling
    const isFilled = this.getCurrentFilledCode().length >= this._codeLength;
    if (!isFilled) {
      return;
    }

    // focusing on the last input if is filled
    setTimeout(() => last.focus());
  }
  private getCurrentFilledCode(): string {
    let code = '';

    for (const input of this.inputs) {
      if (!this.isEmpty(input.value)) {
        code += input.value;
      }
    }

    return code;
  }
  private isEmpty(value: any): boolean {
    return value === null || value === undefined || !value.toString().length;
  }

  onPaste(e: ClipboardEvent, i: number): void {
    e.preventDefault();
    e.stopPropagation();

    const data = e.clipboardData ? e.clipboardData.getData('text').trim() : undefined;

    if (this.isEmpty(data)) {
      return;
    }

    // Convert paste text into iterable
    // tslint:disable-next-line:no-non-null-assertion
    const values = data!.split('');
    let valIndex = 0;

    for (let j = i; j < this.inputs.length; j++) {
      // The values end is reached. Loop exit
      if (valIndex === values.length) {
        break;
      }

      const input = this.inputs[j];
      const val = values[valIndex];

      // Cancel the loop when a value cannot be used
      if (!this.canInputValue(val)) {
        this.setInputValue(input, null);
        this.setStateForInput(input, InputState.reset);
        return;
      }

      this.setInputValue(input, val.toString());
      valIndex++;
    }

    this.inputs[i].blur();
    this.emitChanges();
  }

  private canInputValue(value: any): boolean {
    if (this.isEmpty(value)) {
      return false;
    }

    const isDigitsValue = /^[0-9]+$/.test(value.toString());
    return isDigitsValue || this.isCharsCode || this.isNonDigitsCode;
  }

  private setInputValue(input: HTMLInputElement, value: any): void {
    const isEmpty = this.isEmpty(value);
    const valueClassCSS = 'has-value';
    const emptyClassCSS = 'empty';
    if (isEmpty) {
      input.value = '';
      input.classList.remove(valueClassCSS);
      // tslint:disable-next-line:no-non-null-assertion
      input.parentElement!.classList.add(emptyClassCSS);
    } else {
      input.value = value;
      input.classList.add(valueClassCSS);
      // tslint:disable-next-line:no-non-null-assertion
      input.parentElement!.classList.remove(emptyClassCSS);
    }

    // this.checkConfirmationcodeFilled();
    this.confirmationcodeFilled = this.placeholders.length === this.getCurrentFilledCode().length;
  }
  private setStateForInput(input: HTMLInputElement, state: InputState): void {
    const index = this.inputs.indexOf(input);
    if (index < 0) {
      return;
    }

    this.inputsStates[index] = state;
  }
  private emitChanges(): void {
    setTimeout(() => this.emitCode(), 50);
  }
  private emitCode(): void {
    const code = this.getCurrentFilledCode();

    // this.codeChanged.emit(code);

    if (code.length >= this._codeLength) {
      this.twoFactorCode = code;
    }
  }
  onInput(e: any, i: number): void {
    const target = e.target;
    const value = e.data || target.value;

    if (this.isEmpty(value)) {
      return;
    }

    // only digits are allowed if isCharsCode flag is absent/false
    if (!this.canInputValue(value)) {
      e.preventDefault();
      e.stopPropagation();
      this.setInputValue(target, null);
      this.setStateForInput(target, InputState.reset);
      return;
    }

    const values = value.toString().trim().split('');
    for (let j = 0; j < values.length; j++) {
      const index = j + i;
      if (index > this._codeLength - 1) {
        break;
      }

      this.setInputValue(this.inputs[index], values[j]);
    }

    this.emitChanges();

    const next = i + values.length;
    if (next > this._codeLength - 1) {
      // target.blur();
      return;
    }

    this.inputs[next]?.focus();
  }

  async onKeydown(e: any, i: number): Promise<void> {
    const target = e.target;
    const isTargetEmpty = this.isEmpty(target.value);
    const prev = i - 1;

    // processing only the backspace and delete key events
    const isBackspaceKey = await this.isBackspaceKey(e);
    const isDeleteKey = this.isDeleteKey(e);
    if (!isBackspaceKey && !isDeleteKey) {
      return;
    }

    e.preventDefault();

    this.setInputValue(target, null);
    if (!isTargetEmpty) {
      this.emitChanges();
    }

    // preventing to focusing on the previous field if it does not exist or the delete key has been pressed
    if (prev < 0 || isDeleteKey) {
      return;
    }

    if (isTargetEmpty || this.isPrevFocusableAfterClearing) {
      this.inputs[prev].focus();
    }
  }
  private isDeleteKey(e: any): boolean {
    return (e.key && e.key.toLowerCase() === 'delete') || (e.keyCode && e.keyCode === 46);
  }
  private isEnterKey(e: any): boolean {
    return (e.key && e.key.toLowerCase() === 'enter') || (e.keyCode && e.keyCode === 13);
  }
  private isBackspaceKey(e: any): Promise<boolean> {
    const isBackspace = (e.key && e.key.toLowerCase() === 'backspace') || (e.keyCode && e.keyCode === 8);
    if (isBackspace) {
      return Promise.resolve(true);
    }

    // process only key with placeholder keycode on android devices
    if (!e.keyCode || e.keyCode !== 229) {
      return Promise.resolve(false);
    }

    return new Promise<boolean>((resolve) => {
      setTimeout(() => {
        const input = e.target;
        const isReset = this.getStateForInput(input) === InputState.reset;
        if (isReset) {
          this.setStateForInput(input, InputState.ready);
        }
        // if backspace key pressed the caret will have position 0 (for single value field)
        resolve(input.selectionStart === 0 && !isReset);
      });
    });
  }
  private getStateForInput(input: HTMLInputElement): InputState | undefined {
    const index = this.inputs.indexOf(input);
    return this.inputsStates[index];
  }
  private onInputsListChanges(list: QueryList<ElementRef>): void {
    if (list.length > this.inputs.length) {
      const inputsToAdd = list.filter((item, index) => index > this.inputs.length - 1);
      this.inputs.splice(this.inputs.length, 0, ...inputsToAdd.map((item) => item.nativeElement));
      const states = Array(inputsToAdd.length).fill(InputState.ready);
      this.inputsStates.splice(this.inputsStates.length, 0, ...states);
    } else if (list.length < this.inputs.length) {
      this.inputs.splice(list.length);
      this.inputsStates.splice(list.length);
    }

    // filling the inputs after changing of their count
    this.onInputCodeChanges();
  }

  private onInputCodeChanges(): void {
    if (!this.inputs.length) {
      return;
    }

    if (this.isEmpty(this.code)) {
      this.inputs.forEach((input: HTMLInputElement) => {
        this.setInputValue(input, null);
      });
      return;
    }

    // tslint:disable-next-line:no-non-null-assertion
    const chars = this.code!.toString().trim().split('');
    // checking if all the values are correct
    let isAllCharsAreAllowed = true;
    for (const char of chars) {
      if (!this.canInputValue(char)) {
        isAllCharsAreAllowed = false;
        break;
      }
    }

    this.inputs.forEach((input: HTMLInputElement, index: number) => {
      const value = isAllCharsAreAllowed ? chars[index] : null;
      this.setInputValue(input, value);
    });
  }

  private onCodeLengthChanges(): void {
    if (!this.codeLength) {
      return;
    }

    this._codeLength = this.codeLength;
    if (this._codeLength > this.placeholders.length) {
      const numbers = Array(this._codeLength - this.placeholders.length).fill(1);
      this.placeholders.splice(this.placeholders.length - 1, 0, ...numbers);
    } else if (this._codeLength < this.placeholders.length) {
      this.placeholders.splice(this._codeLength);
    }
  }

  private async ensureCurrentLoginInfo(): Promise<LoginInfo> {
    if (!this.loginInfo) {
      this.loginInfo = await this.userService.getCurrentLoginInfo(this.viewState.showChangePassword).toPromise();
    }
    this.changeDetectorRef.detectChanges();
    return this.loginInfo;
  }
}
